import React, { memo, useEffect, useMemo, useState } from "react";
import { Modal, Spin, Typography, Tag, Tooltip, Divider, Row, Col } from "antd";
import {
  UserOutlined,
  PhoneOutlined,
  MailOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  FilePdfOutlined,
  CalendarOutlined,
  SafetyCertificateOutlined,
} from "@ant-design/icons";
import { useSelector } from "react-redux";
import { getApi } from "utils/apiCall";
import { getApiArrayDetails, getPreSignedUrl } from "utils/MyMUtils";
import { BRAND, FIXED_MODULES, filterAllowedModules, getModuleIcon } from "./staffModalShared";

const { Text, Title } = Typography;
const FALLBACK = "—";

// ─── Helpers ──────────────────────────────────────────────────────────────────

// Asepsis apps show dates as DD-MM-YYYY.
const toDdMmYyyy = (d) => `${String(d.getDate()).padStart(2, "0")}-${String(d.getMonth() + 1).padStart(2, "0")}-${d.getFullYear()}`;

const formatDate = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d)) return dateStr;
  return toDdMmYyyy(d);
};

// ─── Section wrapper ──────────────────────────────────────────────────────────

const Section = memo(({ title, children, style }) => (
  <div style={{
    background: "#fff",
    border: "1px solid #edf2f9",
    borderRadius: 10,
    padding: "16px 18px",
    height: "100%",
    ...style,
  }}>
    <div style={{
      fontSize: 11, fontWeight: 700, color: BRAND,
      textTransform: "uppercase", letterSpacing: "0.06em",
      marginBottom: 14, paddingBottom: 8,
      borderBottom: "1px solid #edf2f9",
    }}>
      {title}
    </div>
    {children}
  </div>
));

// ─── Info row ─────────────────────────────────────────────────────────────────

const InfoRow = memo(({ label, children }) => (
  <div style={{ marginBottom: 12 }}>
    <div style={{ fontSize: 10, color: "#a0b0c4", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 3 }}>
      {label}
    </div>
    <div style={{ fontSize: 13, color: "#101828", fontWeight: 500 }}>
      {children || <span style={{ color: "#d0dde9" }}>{FALLBACK}</span>}
    </div>
  </div>
));

// ─── Compliance card ──────────────────────────────────────────────────────────

const ComplianceCard = memo(({ label, doc }) => {
  const isUploaded     = doc?.status === "uploaded";
  const isExpired      = doc?.status === "expired";
  const isExpiringSoon = doc?.status === "expiring_soon";
  const isActive       = isUploaded || isExpired || isExpiringSoon;

  const bgColor     = isUploaded ? "#f6ffed" : (isExpired || isExpiringSoon) ? "#fff7e6" : "#fafbfc";
  const borderColor = isUploaded ? "#b7eb8f" : (isExpired || isExpiringSoon) ? "#ffd591" : "#f0f0f0";
  const iconBg      = isUploaded ? "#1A8F5C" : isExpired ? "#C0392B" : isExpiringSoon ? "#B4690E" : "#e8e8e8";
  const labelColor  = isActive ? "#101828" : "#bfbfbf";
  const tagColor    = isUploaded ? "success" : isExpired ? "error" : isExpiringSoon ? "warning" : "default";
  const tagText     = isUploaded ? "Uploaded" : isExpired ? "Expired" : isExpiringSoon ? "Expiring Soon" : "Missing";

  return (
    <div style={{
      border: `1px solid ${borderColor}`,
      borderRadius: 8, padding: "10px 12px", marginBottom: 8,
      background: bgColor,
      display: "flex", alignItems: "flex-start", gap: 10,
    }}>
      <div style={{
        width: 30, height: 30, borderRadius: "50%", flexShrink: 0,
        background: iconBg,
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        {isActive
          ? <CheckCircleOutlined style={{ color: "#fff", fontSize: 14 }} />
          : <CloseCircleOutlined style={{ color: "#bfbfbf", fontSize: 14 }} />}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: labelColor, marginBottom: 2 }}>
          {label}
        </div>
        {isActive && doc?.document_link && (
          <div
            onClick={() => getPreSignedUrl(doc.document_link)}
            style={{ fontSize: 11, color: BRAND, display: "inline-flex", alignItems: "center", gap: 4, cursor: "pointer", marginTop: 2 }}
          >
            <FilePdfOutlined /> View Document
          </div>
        )}
        {isActive && doc?.vaccination_date && (
          <div style={{ fontSize: 11, color: "#5B6472", marginTop: 2 }}>
            <CalendarOutlined style={{ marginRight: 4 }} />Vaccinated: {formatDate(doc.vaccination_date)}
          </div>
        )}
        {isActive && doc?.issue_date && (
          <div style={{ fontSize: 11, color: "#5B6472", marginTop: 2 }}>
            <CalendarOutlined style={{ marginRight: 4 }} />Issued: {formatDate(doc.issue_date)}
          </div>
        )}
        {isActive && doc?.expiry_date && (
          <div style={{ fontSize: 11, color: "#5B6472", marginTop: 2, display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
            <span><CalendarOutlined style={{ marginRight: 4 }} />Expires: {formatDate(doc.expiry_date)}</span>
            {doc.days_to_expiry != null && (
              <Tag color={doc.days_to_expiry < 0 ? "red" : doc.days_to_expiry < 30 ? "orange" : "green"} style={{ fontSize: 10, margin: 0 }}>
                {doc.days_to_expiry < 0 ? `${Math.abs(doc.days_to_expiry)}d ago` : `${doc.days_to_expiry}d left`}
              </Tag>
            )}
          </div>
        )}
        {isActive && doc?.ref_number && (
          <div style={{ fontSize: 11, color: "#5B6472", marginTop: 2 }}>
            <SafetyCertificateOutlined style={{ marginRight: 4 }} />Ref: {doc.ref_number}
          </div>
        )}
        {!isActive && (
          <div style={{ fontSize: 11, color: "#bfbfbf" }}>Not uploaded</div>
        )}
      </div>
      <Tag color={tagColor} style={{ fontSize: 11, flexShrink: 0, borderRadius: 4 }}>
        {tagText}
      </Tag>
    </div>
  );
});

// ─── Module chip ──────────────────────────────────────────────────────────────

const ModuleChip = memo(({ name, active }) => {
  const Icon = getModuleIcon(name);
  return (
    <Tooltip title={name} placement="top" mouseEnterDelay={0.2}>
      <div style={{
        width: 34, height: 34,
        display: "flex", alignItems: "center", justifyContent: "center",
        border: `1.5px solid ${active ? BRAND : "#edf2f9"}`,
        borderRadius: 6,
        background: active ? "#e8f0fb" : "#fafbfc",
        flexShrink: 0,
        transition: "all 0.15s ease",
      }}>
        <Icon style={{ fontSize: 15, color: active ? BRAND : "#d0dde9" }} />
      </div>
    </Tooltip>
  );
});

// ─── Facility row ─────────────────────────────────────────────────────────────

const FacilityViewRow = memo(({ facility, allModules }) => {
  const assignedModuleIds = useMemo(
    () => new Set(filterAllowedModules(facility.modules || []).map((m) => m.module_id)),
    [facility.modules]
  );

  return (
    <div style={{
      display: "flex", alignItems: "center",
      padding: "8px 12px",
      border: `1px solid ${BRAND}`,
      borderRadius: 7, marginBottom: 8,
      background: "#f4f8fd",
    }}>
      <div style={{ flex: 1, minWidth: 0, marginRight: 12 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: BRAND }}>{facility.facility_name}</div>
        {facility.state_name && (
          <div style={{ fontSize: 11, color: "#5B6472", marginTop: 1 }}>{facility.state_name}</div>
        )}
      </div>
      <div style={{ width: 1, height: 30, background: "#c8daf0", flexShrink: 0, marginRight: 12 }} />
      <div style={{ display: "flex", gap: 8 }}>
        {allModules.map((mod) => (
          <ModuleChip key={mod.module_id} name={mod.name} active={assignedModuleIds.has(mod.module_id)} />
        ))}
      </div>
    </div>
  );
});

// ─── API mapper ───────────────────────────────────────────────────────────────

const mapApiToView = (data, rolesMap = {}) => {
  const facilityList = Array.isArray(data.facilities) ? data.facilities : [];
  const doc = data.document || {};
  const roleId = data.role_id ?? null;
  const roleName = data.role_name || (roleId != null ? rolesMap[roleId] : null) || "";

  // Always show all FIXED_MODULES as columns — highlight only assigned ones
  // allModules = FIXED_MODULES (all 5 shown)
  // assignedModuleIds per facility = from API response (filtered to allowed)

  return {
    id: data.id,
    firstName: data.first_name || "",
    lastName: data.last_name || "",
    fullName: `${data.first_name || ""} ${data.last_name || ""}`.trim() || "Unnamed Staff",
    mobile: data.mobile || "",
    email: data.email || "",
    username: data.user_name || "",
    isAgencyStaff: Boolean(data.is_agency_staff),
    roleName,
    organisation: data.organization_name || data.organisation_name || "",
    overallStatus: data.overall_status || "",
    overallStatusColor: data.overall_status_color || "#5B6472",
    facilities: facilityList,
    compliance: {
      flu:    doc.flu_vaccination || null,
      covid:  doc.covid_vaccination || null,
      stat:   doc.statutory_declaration || null,
      police: doc.police_check || null,
    },
  };
};

// ─── Main component ───────────────────────────────────────────────────────────

const ViewStaffModal = ({ visible, onClose, staffId }) => {
  const [loading, setLoading] = useState(false);
  const [staff, setStaff]     = useState(null);
  const roles = useSelector((state) => getApiArrayDetails(state.getRole.data, []));

  const rolesMap = useMemo(
    () => Object.fromEntries((roles || []).map((r) => [r.role_id, r.role_name])),
    [roles]
  );

  useEffect(() => {
    if (!visible || !staffId) return;
    let isMounted = true;
    const fetchStaff = async () => {
      setLoading(true);
      const { error, result } = await getApi(`/api/staff/listing/${staffId}`);
      if (!isMounted) return;
      if (!error && result) {
        const data = result.result ?? result;
        setStaff(mapApiToView(data, rolesMap));
      }
      setLoading(false);
    };
    fetchStaff();
    return () => { isMounted = false; };
  }, [visible, staffId, rolesMap]);

  const handleClose = () => { setStaff(null); onClose(); };

  return (
    <Modal
      visible={visible}
      onCancel={handleClose}
      footer={null}
      width={900}
      title={
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: "#101828" }}>Staff Profile</div>
          <div style={{ fontSize: 12, color: "#5B6472", marginTop: 2 }}>Read-only view of staff details</div>
        </div>
      }
      bodyStyle={{ padding: "20px 24px", maxHeight: "78vh", overflowY: "auto", background: "#f7f9fc" }}
      destroyOnClose
    >
      <Spin spinning={loading}>
        {staff && (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

            {/* Header banner */}
            <div style={{
              background: "#fff", border: "1px solid #edf2f9",
              borderRadius: 10, padding: "14px 18px",
              display: "flex", alignItems: "center", gap: 14,
            }}>
              <div style={{
                width: 46, height: 46, borderRadius: "50%", background: BRAND,
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
              }}>
                <UserOutlined style={{ color: "#fff", fontSize: 22 }} />
              </div>
              <div style={{ flex: 1 }}>
                <Title level={5} style={{ margin: 0, color: BRAND }}>{staff.fullName}</Title>
                <Text style={{ fontSize: 12, color: "#5B6472" }}>
                  {staff.roleName || "No role assigned"}
                  {staff.organisation ? ` · ${staff.organisation}` : ""}
                </Text>
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <Tag color={staff.isAgencyStaff ? "blue" : "default"} style={{ fontSize: 12, margin: 0 }}>
                  {staff.isAgencyStaff ? "Agency Staff" : "Direct Staff"}
                </Tag>
                {staff.overallStatus && (
                  <Tag style={{
                    margin: 0, borderRadius: 999, fontWeight: 600, fontSize: 12,
                    borderColor: staff.overallStatusColor,
                    color: staff.overallStatusColor, background: "#fff",
                  }}>
                    {staff.overallStatus}
                  </Tag>
                )}
              </div>
            </div>

            {/* Row 1: Personal Details + Compliance */}
            <Row gutter={16}>
              <Col span={12}>
                <Section title="Personal Details">
                  <InfoRow label="First Name">{staff.firstName}</InfoRow>
                  <InfoRow label="Last Name">{staff.lastName}</InfoRow>
                  <InfoRow label="Mobile">
                    {staff.mobile
                      ? <span><PhoneOutlined style={{ marginRight: 6, color: "#5B6472" }} />{staff.mobile}</span>
                      : null}
                  </InfoRow>
                  <InfoRow label="Email">
                    {staff.email
                      ? <span><MailOutlined style={{ marginRight: 6, color: "#5B6472" }} />{staff.email}</span>
                      : null}
                  </InfoRow>
                  <Divider style={{ margin: "10px 0" }} />
                  <div style={{ fontSize: 11, fontWeight: 700, color: BRAND, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 10 }}>
                    Login
                  </div>
                  <InfoRow label="Username">{staff.username}</InfoRow>
                </Section>
              </Col>
              <Col span={12}>
                <Section title="Compliance">
                  <ComplianceCard label="Flu Vaccination"   doc={staff.compliance.flu} />
                  <ComplianceCard label="COVID Vaccination" doc={staff.compliance.covid} />
                  <ComplianceCard label="Stat Declaration"  doc={staff.compliance.stat} />
                  <ComplianceCard label="Police Check"      doc={staff.compliance.police} />
                </Section>
              </Col>
            </Row>

            {/* Row 2: Facility and Module Access */}
            <Section title="Facility and Module Access">
              {staff.facilities.length === 0 ? (
                <Text style={{ fontSize: 13, color: "#bfbfbf" }}>No facilities assigned.</Text>
              ) : (
                <>
                  {/* Column headers — always show all FIXED_MODULES */}
                  <div style={{ display: "flex", alignItems: "center", padding: "0 12px", marginBottom: 6 }}>
                    <div style={{ flex: 1, fontSize: 10, fontWeight: 700, color: "#a0b0c4", textTransform: "uppercase", letterSpacing: "0.05em", marginRight: 12 }}>
                      Facility
                    </div>
                    <div style={{ width: 1, marginRight: 12 }} />
                    <div style={{ display: "flex", gap: 8 }}>
                      {FIXED_MODULES.map((mod) => {
                        const Icon = getModuleIcon(mod.name);
                        return (
                          <Tooltip key={mod.module_id} title={mod.name} placement="top">
                            <div style={{ width: 34, height: 20, display: "flex", alignItems: "center", justifyContent: "center" }}>
                              <Icon style={{ fontSize: 13, color: "#c8d6e5" }} />
                            </div>
                          </Tooltip>
                        );
                      })}
                    </div>
                  </div>
                  {/* Rows — allModules = FIXED_MODULES, active = assigned only */}
                  {staff.facilities.map((f) => (
                    <FacilityViewRow key={f.facility_id} facility={f} allModules={FIXED_MODULES} />
                  ))}
                </>
              )}
            </Section>

          </div>
        )}
      </Spin>
    </Modal>
  );
};

export default memo(ViewStaffModal);
